import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { getActiveOverviewTab, setActiveOverviewTab } from 'redux/ModelsSlice';

import { TabType } from '../types';
import { DEFAULT_MODEL_TAB_URL, getTabFromUrlValue, getUrlValueFromTab } from '../constants';

const useActiveTab = (initialTab: TabType = 'dashboard') => {
  const dispatch: any = useDispatch();
  const storedTab = useSelector(getActiveOverviewTab) as TabType | undefined;
  const navigate = useNavigate();
  const { modelId, tab } = useParams();
  const tabFromUrl = getTabFromUrlValue(tab ?? null);
  const activeTab = tabFromUrl ?? storedTab ?? initialTab;

  useEffect(() => {
    if (tabFromUrl) {
      // If the user already selected a tab earlier in this session, keep it.
      // Redirect URL to match storedTab instead of overwriting Redux from URL.
      if (storedTab && storedTab !== tabFromUrl) {
        const storedUrlValue = getUrlValueFromTab(storedTab);
        if (modelId) {
          navigate(`/model/${modelId}/${storedUrlValue || DEFAULT_MODEL_TAB_URL}`, {
            replace: true,
          });
        }
        return;
      }

      // First load / no stored tab yet: accept URL tab and hydrate Redux.
      if (!storedTab) {
        dispatch(setActiveOverviewTab(tabFromUrl));
      }
      return;
    }

    const fallbackTab = storedTab ?? initialTab;
    const fallbackUrlValue = getUrlValueFromTab(fallbackTab);
    if (modelId) {
      navigate(`/model/${modelId}/${fallbackUrlValue || DEFAULT_MODEL_TAB_URL}`, { replace: true });
    }

    if (!storedTab) {
      dispatch(setActiveOverviewTab(fallbackTab));
    }
  }, [dispatch, initialTab, modelId, navigate, storedTab, tabFromUrl]);

  const handleTabChange = useCallback(
    (nextTab: TabType) => {
      dispatch(setActiveOverviewTab(nextTab));
      const nextUrlValue = getUrlValueFromTab(nextTab);
      if (modelId) {
        navigate(`/model/${modelId}/${nextUrlValue}`, { replace: false });
      }
    },
    [modelId, navigate, storedTab],
  );

  return {
    activeTab,
    setActiveTab: handleTabChange,
  };
};

export default useActiveTab;
